import React from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingDown, Activity, TrendingUp, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useUnits } from '../../../contexts/UnitsContext'
import { cn } from '../../../lib/utils'

const TYPE_ICON = { cut: TrendingDown, maintain: Activity, bulk: TrendingUp }
const TYPE_COLOR = {
  cut: 'bg-app-primary-100 text-app-primary-600 dark:bg-app-primary-900/30 dark:text-app-primary-400',
  maintain: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  bulk: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
}

function formatDate(iso, lang) {
  if (!iso) return ''
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(lang || 'en', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  } catch {
    return iso
  }
}

export function PhaseCard({ phase, isCurrent, onEdit, onDelete }) {
  const { t, i18n } = useTranslation()
  const { weightUnit, formatWeight } = useUnits()
  const Icon = TYPE_ICON[phase.phase_type] || Activity

  const typeLabel = t(`phoenixTracker.phases.type.${phase.phase_type}`)
  const title = phase.name || typeLabel

  const dateRange = phase.end_date
    ? `${formatDate(phase.start_date, i18n.language)} → ${formatDate(phase.end_date, i18n.language)}`
    : `${formatDate(phase.start_date, i18n.language)} → ${t('phoenixTracker.phases.list.ongoing')}`

  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className={cn('rounded-full p-2 flex-shrink-0', TYPE_COLOR[phase.phase_type])}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <p className="text-sm font-semibold text-foreground truncate">{title}</p>
              {isCurrent && (
                <span className="text-[10px] uppercase tracking-wide font-bold text-green-600 dark:text-green-400">
                  {t('phoenixTracker.phases.list.active')}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{dateRange}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatWeight(phase.start_weight_kg)} {weightUnit}
              {phase.goal_weight_kg != null && (
                <> → {formatWeight(phase.goal_weight_kg)} {weightUnit}</>
              )}
            </p>
            {phase.notes && (
              <p className="text-xs text-foreground mt-1.5 whitespace-pre-wrap">{phase.notes}</p>
            )}
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => onEdit(phase)}
              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
              aria-label={t('phoenixTracker.phases.form.edit')}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(phase)}
              className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500"
              aria-label={t('phoenixTracker.phases.delete')}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
